import { api } from "./apiSlice";

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export interface BloodRequestRecord {
  id: string;
  bloodGroup: string;
  unitsRequired: number;
  urgencyLevel: string;
  requestReason: string;
  requestDeliveryDate: string;
  requestDeliveryTime: string;
  medicalEstablishmentId: string;
  requestingBloodBankId: string | null;
  additionalNotes: string | null;
  status: string;
  createdAt: string;
  /** Only present on getRequestById — the backend's getById joins both. */
  medEstName?: string;
  bloodBankName?: string;
}

export interface CreateRequestInput {
  bloodGroup: string;
  unitsRequired: number;
  urgencyLevel: string;
  requestReason: string;
  requestDeliveryDate: string;
  requestDeliveryTime: string;
  medicalEstablishmentId: string;
  requestingBloodBankId?: string;
  additionalNotes?: string;
}

export interface DispatchRequestInput {
  id: string;
  bloodId: string;
  deliveryVehicle: string;
  dispatchDateTime?: string;
  deliveryDateTime: string;
}

export interface DispatchRequestResult {
  request: BloodRequestRecord;
  bloodRequest: { id: string; bloodId: string; status: string };
  transit: { id: string; transitStatus: string; receiverHospitalId: string; dispatchDateTime: string; deliveryDateTime: string; deliveryVehicle: string; bloodRequestId: string | null; bloodId: string };
}

export const requestsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getPendingByRecipient: builder.query<BloodRequestRecord[], string>({
      query: (medicalEstablishmentId) => `/requests/pending/by-recipient?medicalEstablishmentId=${medicalEstablishmentId}`,
      transformResponse: (res: ApiResponse<{ requests: BloodRequestRecord[] }>) => res.data.requests,
      providesTags: ["Requests"],
    }),
    getPendingByRequester: builder.query<BloodRequestRecord[], string>({
      query: (medicalEstablishmentId) => `/requests/pending/by-requester?medicalEstablishmentId=${medicalEstablishmentId}`,
      transformResponse: (res: ApiResponse<{ requests: BloodRequestRecord[] }>) => res.data.requests,
      providesTags: ["Requests"],
    }),
    getRequestsSummary: builder.query<{ total: number; incoming: number; outgoing: number; inTransit: number }, string>({
      query: (medicalEstablishmentId) => `/requests/summary?medicalEstablishmentId=${medicalEstablishmentId}`,
      transformResponse: (res: ApiResponse<{ total: number; incoming: number; outgoing: number; inTransit: number }>) => res.data,
      providesTags: ["Requests"],
    }),
    getRequestById: builder.query<BloodRequestRecord, string>({
      query: (id) => `/requests/${id}`,
      transformResponse: (res: ApiResponse<BloodRequestRecord>) => res.data,
      providesTags: (_r, _e, id) => [{ type: "Requests", id }],
    }),
    createRequest: builder.mutation<BloodRequestRecord, CreateRequestInput>({
      query: (body) => ({ url: "/requests", method: "POST", body }),
      transformResponse: (res: ApiResponse<BloodRequestRecord>) => res.data,
      invalidatesTags: ["Requests"],
    }),
    dispatchRequest: builder.mutation<DispatchRequestResult, DispatchRequestInput>({
      query: ({ id, ...body }) => ({ url: `/requests/${id}/dispatch`, method: "POST", body }),
      transformResponse: (res: ApiResponse<DispatchRequestResult>) => res.data,
      invalidatesTags: ["Requests", "Blood", "Inventory"],
    }),
  }),
});

export const {
  useGetPendingByRecipientQuery,
  useGetPendingByRequesterQuery,
  useGetRequestsSummaryQuery,
  useGetRequestByIdQuery,
  useCreateRequestMutation,
  useDispatchRequestMutation,
} = requestsApi;
